import { isDevPreview } from '../boot/devPreview';
import { sb } from './supabase';
import { logActivity } from './repo';

/**
 * 기본 도면(매장 현황 도면) 저장소
 *
 * CAD 관리자가 "이 매장의 지금 모습"을 한 번 등록해 두면, 설계자는 그것을
 * 복사해서 자기 안을 만든다. 매번 벽체부터 다시 그리지 않아도 된다.
 *
 * ── 권한 ────────────────────────────────────────────────────────────────
 *   등록·수정·삭제는 관리자만. 일반 사용자는 읽고 복사만 한다.
 *   화면에서도 막고 RLS(0010)에서도 막는다 — 한쪽만 막으면 뚫리거나,
 *   사용자가 "왜 안 되는지" 를 모른다.
 *
 * ── 복사의 의미 ──────────────────────────────────────────────────────────
 *   ★ 복사지 참조가 아니다. 기본 도면을 나중에 고쳐도 이미 만들어진 도면은
 *     그대로여야 한다. 연결해 두면 "현황이 갱신되자 남이 검토 중이던 안이
 *     통째로 바뀌는" 사고가 난다.
 */

function fail(where: string, error: { message: string } | null): void {
  if (error) throw new Error(`${where}: ${error.message}`);
}

export interface BaseDrawingRow {
  id: string;
  name: string;
  storeCode: string | null;
  floorName: string | null;
  description: string | null;
  active: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** 목록 조회에서는 채우지 않는다 — 본문이 크기 때문 */
  document?: unknown;
}

const SELECT_COLS =
  'id, name, store_code, floor_name, description, active, created_by, created_at, updated_at';

function mapRow(r: Record<string, unknown>): BaseDrawingRow {
  return {
    id: r.id as string,
    name: r.name as string,
    storeCode: (r.store_code as string | null) ?? null,
    floorName: (r.floor_name as string | null) ?? null,
    description: (r.description as string | null) ?? null,
    active: r.active as boolean,
    createdBy: r.created_by as string,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
    document: r.document_json,
  };
}

/* -------------------------------------------------------------------------- *
 * 조회
 * -------------------------------------------------------------------------- */

export async function listBaseDrawings(includeInactive = false): Promise<BaseDrawingRow[]> {
  if (isDevPreview()) {
    return DEV_BASE.filter((b) => includeInactive || b.active);
  }

  const client = sb();
  let q = client.from('cad_base_drawing').select(SELECT_COLS).order('name', { ascending: true });
  // 비활성은 관리자만 볼 수 있다(RLS). 화면에서도 기본은 감춘다.
  if (!includeInactive) q = q.eq('active', true);

  const { data, error } = await q;
  fail('기본 도면 목록 조회', error);
  return (data ?? []).map(mapRow);
}

/** 본문까지 포함해 하나를 읽는다 — 복사할 때만 부른다. */
export async function loadBaseDrawing(id: string): Promise<BaseDrawingRow> {
  if (isDevPreview()) {
    const found = DEV_BASE.find((b) => b.id === id);
    if (!found) throw new Error('기본 도면을 찾을 수 없습니다.');
    return found;
  }

  const client = sb();
  const { data, error } = await client
    .from('cad_base_drawing')
    .select(`${SELECT_COLS}, document_json`)
    .eq('id', id)
    .single();
  fail('기본 도면 조회', error);
  return mapRow(data as Record<string, unknown>);
}

/* -------------------------------------------------------------------------- *
 * 등록 · 수정 · 삭제 (관리자 전용)
 * -------------------------------------------------------------------------- */

export interface NewBaseDrawing {
  name: string;
  storeCode?: string | null;
  floorName?: string | null;
  description?: string | null;
  document: unknown;
}

export async function createBaseDrawing(input: NewBaseDrawing): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다.');
  if (!input.name.trim()) throw new Error('이름을 입력하세요.');

  const client = sb();
  const { error } = await client.from('cad_base_drawing').insert({
    name: input.name.trim(),
    store_code: input.storeCode || null,
    floor_name: input.floorName || null,
    description: input.description || null,
    document_json: input.document,
  });
  fail('기본 도면 등록', error);

  await logActivity('base_drawing.create', { payload: { name: input.name.trim() } });
}

export async function updateBaseDrawing(
  id: string,
  patch: Partial<Pick<BaseDrawingRow, 'name' | 'storeCode' | 'floorName' | 'description' | 'active'>>
): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다.');

  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name.trim();
  if (patch.storeCode !== undefined) row.store_code = patch.storeCode || null;
  if (patch.floorName !== undefined) row.floor_name = patch.floorName || null;
  if (patch.description !== undefined) row.description = patch.description || null;
  if (patch.active !== undefined) row.active = patch.active;
  if (Object.keys(row).length === 0) return;

  const client = sb();
  const { error } = await client.from('cad_base_drawing').update(row).eq('id', id);
  fail('기본 도면 수정', error);

  await logActivity('base_drawing.update', { payload: { id, ...row } });
}

export async function deleteBaseDrawing(id: string, name: string): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다.');

  const client = sb();
  const { error } = await client.from('cad_base_drawing').delete().eq('id', id);
  fail('기본 도면 삭제', error);

  await logActivity('base_drawing.delete', { payload: { id, name } });
}

/* -------------------------------------------------------------------------- *
 * 개발 미리보기
 * -------------------------------------------------------------------------- */

const DEV_BASE: BaseDrawingRow[] = [
  {
    id: 'base-1',
    name: '수원점 1F 현황',
    storeCode: 'S-101',
    floorName: '1F',
    description: '2026년 3월 실측 기준. 벽체·기둥·소방 설비 포함.',
    active: true,
    createdBy: 'dev@localhost',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    document: { schema: 1, unit: 'um', entities: [], layers: [] },
  },
  {
    id: 'base-2',
    name: '동탄점 B1 현황',
    storeCode: 'S-207',
    floorName: 'B1',
    description: '신규 출점 기준 도면.',
    active: true,
    createdBy: 'dev@localhost',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    document: { schema: 1, unit: 'um', entities: [], layers: [] },
  },
];
